/**
 * Floating image carousel — background + slides on one stage track (translate only).
 * Wrapped in IIFE so the script can load from multiple sections on one page.
 */
(() => {
  if (customElements.get('floating-image-carousel')) return;

const FIC_SLIDE_WIDTH = 680;
const FIC_SLIDE_HEIGHT = 526;
const FIC_IMAGE_HEIGHT = 336;
const FIC_CARD_GAP = 16;
const FIC_TRANSITION_MS = 640;
const FIC_TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const FIC_PASS_HIDE_MS = 220;

class FloatingImageCarousel extends HTMLElement {
  constructor() {
    super();
    this.current = 0;
    this.trackX = 0;
    this.animating = false;
    this.trackBuilt = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktopMq = window.matchMedia('(min-width: 990px)');
    this.onDesktopMqChange = () => this.render(false);
    this.onCardsTrackScroll = () => this.syncCurrentFromScroll(false);
    this._trackHeightRaf = 0;
    this._trackHeightObserver = null;
    this._trackCardMaxHeight = 0;
    this._transitionSeq = 0;
  }

  beginTransition() {
    this._transitionSeq += 1;
    this.animating = true;
    this.classList.add('is-animating');
    this.setAttribute('aria-busy', 'true');
    this.setNavigationLocked(true);
    this.updateNav();
    this.cancelAllCarouselAnimations();
    return this._transitionSeq;
  }

  isCurrentTransition(seq) {
    return seq === this._transitionSeq;
  }

  cancelAllCarouselAnimations() {
    this.stageTrack?.getAnimations().forEach((animation) => animation.cancel());

    const elements = [this.backgroundEl, ...(this.trackCards || [])];
    elements.forEach((el) => {
      if (!el) return;
      el.getAnimations().forEach((animation) => animation.cancel());
    });
  }

  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-fic-slide]')];
    if (this.slides.length === 0) return;

    this.hasBackground = this.dataset.ficHasBackground === 'true';
    this.showBackground = this.hasBackground;

    this.stage = this.querySelector('.fic__stage');
    this.stageTrack = this.querySelector('[data-fic-stage-track]');
    this.backgroundEl = this.querySelector('[data-fic-background]');
    this.heroWrap = this.querySelector('[data-fic-hero]');
    this.heroImage = this.querySelector('[data-fic-hero-image]');
    this.heroContent = this.querySelector('[data-fic-hero-content]');
    this.cardsTrack = this.querySelector('[data-fic-cards-track]');
    this.liveRegion = this.querySelector('[data-fic-live]');
    this.prevBtn = this.querySelector('[data-fic-prev]');
    this.nextBtn = this.querySelector('[data-fic-next]');

    this.onPrevNavClick = (event) => this.onNavClick(event, -1);
    this.onNextNavClick = (event) => this.onNavClick(event, 1);
    this.prevBtn?.addEventListener('click', this.onPrevNavClick, true);
    this.nextBtn?.addEventListener('click', this.onNextNavClick, true);

    this.desktopMq.addEventListener('change', this.onDesktopMqChange);

    this.buildTrack();
    this.bindMobileScroll();
    this.render(false);
    this.updateNav();
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrevNavClick, true);
    this.nextBtn?.removeEventListener('click', this.onNextNavClick, true);
    this.desktopMq.removeEventListener('change', this.onDesktopMqChange);
    this.cardsTrack?.removeEventListener('scroll', this.onCardsTrackScroll);
    this.unbindTrackCardHeightSync();
  }

  onNavClick(event, delta) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.usesMobileSwipe() || this.slideCount < 1 || this.animating) return;

    this.go(delta);
  }

  setNavigationLocked(locked) {
    [this.prevBtn, this.nextBtn].forEach((btn) => {
      if (!btn) return;

      btn.disabled = locked;
      btn.setAttribute('aria-disabled', locked ? 'true' : 'false');
      btn.classList.toggle('fic__nav--locked', locked);

      if (locked) {
        btn.setAttribute('tabindex', '-1');
      } else {
        btn.removeAttribute('tabindex');
      }
    });

    this.classList.toggle('fic--nav-locked', locked);

    if (locked) {
      this.setAttribute('inert', '');
    } else {
      this.removeAttribute('inert');
    }
  }

  tryAcquireNavigation() {
    if (this.animating) return false;

    this.beginTransition();
    return true;
  }

  usesEqualHeightCards() {
    return this.usesMobileSwipe() || this.usesTrackMotion();
  }

  unbindTrackCardHeightSync() {
    if (this._trackHeightRaf) {
      cancelAnimationFrame(this._trackHeightRaf);
      this._trackHeightRaf = 0;
    }

    this._trackHeightObserver?.disconnect();
    this._trackHeightObserver = null;
  }

  clearTrackCardHeights() {
    this.trackCards?.forEach((card) => {
      card.style.minHeight = '';
    });
  }

  scheduleTrackCardHeightSync() {
    if (!this.usesEqualHeightCards() || !this.trackCards?.length || this.animating) return;

    if (this._trackHeightRaf) cancelAnimationFrame(this._trackHeightRaf);

    this._trackHeightRaf = requestAnimationFrame(() => {
      this._trackHeightRaf = 0;
      this.syncTrackCardHeights();
    });
  }

  measureTrackCardMaxHeight() {
    if (!this.trackCards?.length) return 0;

    this.trackCards.forEach((card) => {
      card.style.minHeight = '';
    });

    const hiddenStates = this.trackCards.map((card) => card.hidden);
    this.trackCards.forEach((card) => {
      card.hidden = false;
    });

    const maxHeight = this.trackCards.reduce(
      (max, card) => Math.max(max, card.getBoundingClientRect().height),
      0
    );

    this.trackCards.forEach((card, index) => {
      card.hidden = hiddenStates[index];
    });

    const measured = maxHeight > 0 ? Math.ceil(maxHeight) : 0;
    if (measured > 0) {
      this._trackCardMaxHeight = measured;
    }

    return this._trackCardMaxHeight;
  }

  applyTrackCardMinHeight() {
    if (!this._trackCardMaxHeight || !this.trackCards?.length) return;

    const minHeight = `${this._trackCardMaxHeight}px`;
    this.trackCards.forEach((card) => {
      card.style.minHeight = minHeight;
    });
  }

  stabilizeTrackCardHeights() {
    if (!this._trackCardMaxHeight) {
      this.measureTrackCardMaxHeight();
    }

    this.applyTrackCardMinHeight();
  }

  syncTrackCardHeights() {
    if (!this.usesEqualHeightCards() || !this.trackCards?.length || this.animating) return;

    this.measureTrackCardMaxHeight();
    this.applyTrackCardMinHeight();
  }

  bindTrackCardHeightSync() {
    this.unbindTrackCardHeightSync();
    if (!this.usesEqualHeightCards() || !this.cardsTrack) return;

    this.scheduleTrackCardHeightSync();

    if (typeof ResizeObserver === 'undefined') return;

    this._trackHeightObserver = new ResizeObserver(() => this.scheduleTrackCardHeightSync());
    this._trackHeightObserver.observe(this.cardsTrack);
    this.trackCards.forEach((card) => this._trackHeightObserver.observe(card));
  }

  isDesktop() {
    return this.desktopMq.matches;
  }

  usesMobileSwipe() {
    return !this.isDesktop() && this.hasBackground && this.slideCount > 0;
  }

  bindMobileScroll() {
    if (!this.cardsTrack) return;
    this.cardsTrack.removeEventListener('scroll', this.onCardsTrackScroll);
    if (this.usesMobileSwipe()) {
      this.cardsTrack.addEventListener('scroll', this.onCardsTrackScroll, { passive: true });
    }
  }

  get slideCount() {
    return this.slides.length;
  }

  getMaxForwardIndex() {
    if (this.slideCount < 1) return -1;

    if (this.usesTrackMotion() && this.hasBackground) {
      return Math.max(0, this.slideCount - 2);
    }

    return this.slideCount - 1;
  }

  get stepPx() {
    return FIC_SLIDE_WIDTH + FIC_CARD_GAP;
  }

  usesTrackMotion() {
    return this.isDesktop() && !this.reducedMotion && !!this.stageTrack;
  }

  shouldAnimate() {
    return this.usesTrackMotion();
  }

  getSliderRadius() {
    const parsed = parseInt(this.dataset.ficSliderRadius, 10);
    return Number.isFinite(parsed) ? parsed : 10;
  }

  getHeroOverlapPx() {
    const styles = getComputedStyle(this);
    return parseFloat(styles.getPropertyValue('--fic-hero-overlap')) || 20;
  }

  getBgStepPx() {
    const styles = getComputedStyle(this);
    const bgWidth = parseFloat(styles.getPropertyValue('--fic-bg-width')) || 824;
    return bgWidth - this.getHeroOverlapPx();
  }

  usesCollapsedTrack() {
    return (
      this.usesTrackMotion() &&
      !this.animating &&
      !(this.hasBackground && this.showBackground)
    );
  }

  bgIncludedInTrackLayout() {
    return !!(this.hasBackground && this.backgroundEl && !this.backgroundEl.hidden);
  }

  getTargetTrackX({
    showBackground = this.showBackground,
    current = this.current,
    layout = 'auto',
    bgInLayout = this.bgIncludedInTrackLayout(),
  } = {}) {
    if (this.hasBackground && showBackground) return 0;

    const collapsed =
      layout === 'collapsed' ||
      (layout === 'auto' && this.usesCollapsedTrack());

    if (collapsed) return 0;

    if (this.hasBackground && bgInLayout) {
      return -this.getBgStepPx() - current * this.stepPx;
    }

    return -current * this.stepPx;
  }

  settleBgForwardLanding() {
    this.showBackground = false;
    this.updateLayoutMode();
    this.setStageTransform(0);
  }

  collapsePassedTrack() {
    if (!this.usesTrackMotion()) return;

    if (this.backgroundEl) {
      this.backgroundEl.hidden = this.hasBackground && !this.showBackground;
      if (this.showBackground) {
        this.backgroundEl.classList.remove('is-passed-faded');
        this.backgroundEl.style.opacity = '';
      }
    }

    this.trackCards?.forEach((card, index) => {
      if (this.showBackground) {
        card.hidden = false;
        card.classList.remove('is-passed-faded');
        card.style.opacity = '';
        return;
      }

      card.hidden = index < this.current;
      card.classList.remove('is-passed-faded');
      card.style.opacity = '';
    });

    this.setStageTransform(this.getTargetTrackX());
  }

  getForwardHideTargets(nextIndex) {
    if (this.showBackground && this.backgroundEl) {
      return [this.backgroundEl];
    }

    const targets = [];

    if (
      this.showBackground &&
      this.hasBackground &&
      this.backgroundEl &&
      !this.backgroundEl.hidden
    ) {
      targets.push(this.backgroundEl);
    }

    this.trackCards?.forEach((card, index) => {
      if (index < nextIndex) targets.push(card);
    });

    return targets.filter((el) => el && !el.hidden);
  }

  hidePassedElements(elements) {
    elements.forEach((el) => {
      el.getAnimations().forEach((animation) => animation.cancel());
      el.style.opacity = '';
      el.hidden = true;
    });
  }

  fadeOutPassedElements(elements, duration = FIC_PASS_HIDE_MS) {
    const visible = elements.filter((el) => el && !el.hidden);

    if (!visible.length) return Promise.resolve();

    if (duration <= 0 || this.reducedMotion) {
      visible.forEach((el) => {
        el.style.opacity = '0';
      });
      return Promise.resolve();
    }

    return Promise.all(
      visible.map((el) =>
        el
          .animate([{ opacity: 1 }, { opacity: 0 }], {
            duration,
            easing: 'ease-out',
            fill: 'forwards',
          })
          .finished.catch(() => {})
      )
    );
  }

  runForwardToIndex(nextIndex) {
    if (!this.animating) this.beginTransition();
    const seq = this._transitionSeq;
    const targets = this.getForwardHideTargets(nextIndex);
    const leavingBg = this.showBackground;

    const runMotion = () => {
      if (!this.isCurrentTransition(seq)) return;
      const wasBackground = leavingBg;

      targets.forEach((el) => {
        el.classList.add('is-passed-faded');
        if (el !== this.backgroundEl) {
          el.hidden = false;
        }
      });

      if (this.backgroundEl && !leavingBg) {
        this.backgroundEl.hidden = true;
        this.backgroundEl.classList.remove('is-passed-faded');
      }

      this.trackCards?.forEach((card) => {
        card.hidden = false;
      });

      this.stabilizeTrackCardHeights();

      const bgInLayout = leavingBg || this.bgIncludedInTrackLayout();
      const fromX = wasBackground
        ? 0
        : this.getTargetTrackX({
            showBackground: false,
            current: this.current,
            layout: 'full',
            bgInLayout,
          });
      const toX = wasBackground
        ? -this.getBgStepPx()
        : this.getTargetTrackX({
            showBackground: false,
            current: nextIndex,
            layout: 'full',
            bgInLayout,
          });

      if (!wasBackground) {
        this.updateLayoutMode();
      }

      this.setStageTransform(fromX);

      this.animateStageTransform(toX, {
        transitionSeq: seq,
        onComplete: () => {
          if (!this.isCurrentTransition(seq)) return;

          targets.forEach((el) => {
            el.classList.remove('is-passed-faded');
            el.style.opacity = '';
          });
          this.hidePassedElements(targets);

          if (wasBackground) {
            this.settleBgForwardLanding();
          }

          this.finishTrackTransition(
            {
              showBackground: false,
              current: nextIndex,
              skipHeightRemeasure: wasBackground,
            },
            seq
          );
        },
      });
    };

    if (!targets.length) {
      runMotion();
      return;
    }

    this.fadeOutPassedElements(targets).then(() => {
      if (!this.isCurrentTransition(seq)) return;
      runMotion();
    });
  }

  instantForwardToIndex(nextIndex) {
    const seq = this.beginTransition();
    const targets = this.getForwardHideTargets(nextIndex);

    if (targets.length) {
      this.hidePassedElements(targets);
    }

    this.finishTrackTransition({ showBackground: false, current: nextIndex, announce: true }, seq);
  }

  getBackwardRevealTargets(prevIndex, returningToBg = false) {
    if (returningToBg && this.backgroundEl) {
      return [this.backgroundEl];
    }

    const targets = [];

    this.trackCards?.forEach((card, index) => {
      if (index < this.current && index <= prevIndex && card.hidden) {
        targets.push(card);
      }
    });

    return targets;
  }

  runBackwardToIndex(prevIndex, { returningToBg = false } = {}) {
    if (!this.animating) this.beginTransition();
    const seq = this._transitionSeq;
    const revealTargets = this.getBackwardRevealTargets(prevIndex, returningToBg);
    const enteringBg = returningToBg;

    const clearFaded = (el) => {
      if (!el) return;
      el.classList.remove('is-passed-faded');
      el.style.opacity = '';
    };

    if (enteringBg) {
      this.showBackground = true;
      this.updateLayoutMode();
    }

    revealTargets.forEach((el) => {
      el.hidden = false;
      clearFaded(el);
    });

    if (this.backgroundEl && !enteringBg) {
      this.backgroundEl.hidden = true;
      clearFaded(this.backgroundEl);
    }

    this.trackCards?.forEach((card) => {
      card.hidden = false;
      clearFaded(card);
    });

    if (enteringBg && this.backgroundEl) {
      this.backgroundEl.hidden = false;
      clearFaded(this.backgroundEl);
    }

    this.stabilizeTrackCardHeights();

    const fromX = enteringBg
      ? this.getTargetTrackX({
          showBackground: false,
          current: 0,
          layout: 'full',
          bgInLayout: true,
        })
      : this.getTargetTrackX({
          showBackground: false,
          current: this.current,
          layout: 'full',
          bgInLayout: false,
        });
    const toX = enteringBg
      ? 0
      : this.getTargetTrackX({
          showBackground: false,
          current: prevIndex,
          layout: 'full',
          bgInLayout: false,
        });

    this.updateLayoutMode();
    this.setStageTransform(fromX);

    this.animateStageTransform(toX, {
      transitionSeq: seq,
      onComplete: () => {
        if (!this.isCurrentTransition(seq)) return;

        this.finishTrackTransition(
          {
            showBackground: enteringBg,
            current: prevIndex,
          },
          seq
        );
      },
    });
  }

  instantBackwardToIndex(prevIndex, { returningToBg = false } = {}) {
    const seq = this.beginTransition();
    const revealTargets = this.getBackwardRevealTargets(prevIndex, returningToBg);

    revealTargets.forEach((el) => {
      el.hidden = false;
      el.style.opacity = '';
      el.classList.remove('is-passed-faded');
    });

    this.finishTrackTransition(
      {
        showBackground: returningToBg,
        current: prevIndex,
        announce: true,
      },
      seq
    );
  }

  applyHeroChrome(slide, mediaEl) {
    if (!slide || !mediaEl) return;

    const hasBorder = slide.dataset.ficImageBorder === 'true';

    mediaEl.style.borderRadius = '';
    mediaEl.classList.toggle('fic__media--border', hasBorder);
  }

  cloneChildren(from, to) {
    to.replaceChildren();
    if (!from) return;

    [...from.children].forEach((child) => {
      to.appendChild(child.cloneNode(true));
    });
  }

  resolveLazyImages(root) {
    if (!root) return;

    root.querySelectorAll('img').forEach((img) => {
      const media = img.closest('.media');
      if (!media) return;

      const markLoaded = () => {
        img.classList.add('loaded');
        media.classList.remove('loading');
      };

      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
        return;
      }

      if (img.loading === 'lazy') img.loading = 'eager';

      if (!media.classList.contains('loading')) {
        media.classList.add('loading');
      }

      img.addEventListener('load', () => {
        markLoaded();
        this.scheduleTrackCardHeightSync();
      }, { once: true });
      img.addEventListener('error', () => media.classList.remove('loading'), { once: true });
    });

    this.scheduleTrackCardHeightSync();
  }

  buildTrackCard(slide, index) {
    const card = document.createElement('article');
    const hasContent = slide.dataset.ficHasContent === 'true';

    card.className = 'fic__slide-card';
    if (hasContent) card.classList.add('fic__slide-card--with-content');
    card.dataset.ficTrackIndex = String(index);
    card.style.borderRadius = `${this.getSliderRadius()}px`;

    const imageSource = slide.querySelector('[data-fic-hero-image]');
    if (imageSource?.children.length) {
      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'fic__peek-media';
      if (slide.dataset.ficImageBorder === 'true') {
        mediaWrap.classList.add('fic__media--border');
      }
      this.cloneChildren(imageSource, mediaWrap);
      card.appendChild(mediaWrap);
    }

    const contentSource = slide.querySelector('[data-fic-hero-content]');
    if (contentSource && !contentSource.hidden && contentSource.innerHTML.trim()) {
      const contentWrap = document.createElement('div');
      contentWrap.className = 'fic__hero-content';
      this.cloneChildren(contentSource, contentWrap);
      card.appendChild(contentWrap);
    }

    return card;
  }

  buildTrack() {
    if (!this.cardsTrack) return;

    const existingCards = [...this.cardsTrack.querySelectorAll('[data-fic-track-card], .fic__slide-card')];

    if (existingCards.length > 0) {
      this.trackCards = existingCards;
      this.resolveLazyImages(this.cardsTrack);
      this.trackBuilt = true;
      this.scheduleTrackCardHeightSync();
      return;
    }

    this.cardsTrack.replaceChildren();
    this.trackCards = this.slides.map((slide, index) => {
      const card = this.buildTrackCard(slide, index);
      card.dataset.ficTrackCard = '';
      this.cardsTrack.appendChild(card);
      return card;
    });

    this.resolveLazyImages(this.cardsTrack);
    this.trackBuilt = true;
    this.scheduleTrackCardHeightSync();
  }

  setStageTransform(x, persist = true) {
    if (!this.stageTrack) return;

    this.stageTrack.getAnimations().forEach((animation) => animation.cancel());
    this.stageTrack.style.transform = `translate3d(${x}px, 0, 0)`;

    if (persist) this.trackX = x;
  }

  syncStageToCurrent(animate = false) {
    if (!this.usesTrackMotion()) return;

    const target = this.getTargetTrackX();
    this.setStageTransform(target, !animate);

    if (animate) {
      this.animateStageTransform(target);
    }
  }

  animateStageTransform(toX, { duration = FIC_TRANSITION_MS, onComplete, transitionSeq } = {}) {
    const seq = transitionSeq ?? this._transitionSeq;

    if (!this.stageTrack) {
      if (this.isCurrentTransition(seq)) onComplete?.();
      return Promise.resolve();
    }

    const fromX = this.trackX;

    if (Math.abs(fromX - toX) < 1) {
      if (!this.isCurrentTransition(seq)) return Promise.resolve();
      this.setStageTransform(toX);
      onComplete?.();
      return Promise.resolve();
    }

    if (duration <= 0 || this.reducedMotion) {
      if (!this.isCurrentTransition(seq)) return Promise.resolve();
      this.setStageTransform(toX);
      onComplete?.();
      return Promise.resolve();
    }

    this.classList.add('is-track-animating');

    const animation = this.stageTrack.animate(
      [
        { transform: `translate3d(${fromX}px, 0, 0)` },
        { transform: `translate3d(${toX}px, 0, 0)` },
      ],
      { duration, easing: FIC_TRANSITION_EASING, fill: 'forwards' }
    );

    return animation.finished
      .catch(() => {})
      .finally(() => {
        animation.cancel();
        if (!this.isCurrentTransition(seq)) {
          this.classList.remove('is-track-animating');
          return;
        }

        this.setStageTransform(toX);
        this.classList.remove('is-track-animating');
        onComplete?.();
      });
  }

  finishTrackTransition(
    { showBackground, current, announce = false, skipHeightRemeasure = false },
    transitionSeq
  ) {
    if (transitionSeq !== undefined && !this.isCurrentTransition(transitionSeq)) return;

    this.showBackground = showBackground;
    this.current = current;
    this.animating = false;
    this.classList.remove('is-animating');
    this.removeAttribute('aria-busy');
    this.setNavigationLocked(false);
    this.updateLayoutMode();
    this.collapsePassedTrack();

    if (showBackground && this.backgroundEl) {
      this.backgroundEl.hidden = false;
      this.backgroundEl.classList.remove('is-passed-faded');
      this.backgroundEl.style.opacity = '';
    }

    if (skipHeightRemeasure && this._trackCardMaxHeight) {
      this.applyTrackCardMinHeight();
    } else if (showBackground && this._trackCardMaxHeight) {
      this.applyTrackCardMinHeight();
    } else {
      this.scheduleTrackCardHeightSync();
    }

    this.updateNav();

    if (announce) {
      this.announceSlide(true);
    }
  }

  updateLayoutMode() {
    const mobileSwipe = this.usesMobileSwipe();

    this.classList.toggle('is-bg-view', this.hasBackground && this.showBackground);
    this.classList.toggle(
      'fic--bg-overlap',
      this.hasBackground &&
        this.slideCount > 0 &&
        (mobileSwipe || (this.showBackground && !mobileSwipe))
    );
    this.classList.toggle('fic--track-mode', this.usesTrackMotion());
    this.classList.toggle('fic--mobile-swipe', mobileSwipe);
    this.classList.toggle('fic--equal-track-cards', this.usesEqualHeightCards());
  }

  updateNav() {
    if (this.usesMobileSwipe()) {
      if (this.prevBtn) {
        this.prevBtn.hidden = true;
        this.prevBtn.disabled = true;
      }
      if (this.nextBtn) {
        this.nextBtn.hidden = true;
        this.nextBtn.disabled = true;
      }
      return;
    }

    if (this.prevBtn) {
      if (this.hasBackground) {
        const hideOnBg = this.showBackground;
        this.prevBtn.hidden = false;
        this.prevBtn.classList.toggle('fic__nav--reserve', hideOnBg);
      } else {
        this.prevBtn.classList.remove('fic__nav--reserve');
        this.prevBtn.hidden = this.current <= 0;
      }
      this.prevBtn.disabled =
        this.animating || this.prevBtn.hidden || this.prevBtn.classList.contains('fic__nav--reserve');
    }

    if (this.nextBtn) {
      const maxForwardIndex = this.getMaxForwardIndex();
      if (this.showBackground) {
        this.nextBtn.hidden = this.slideCount < 1;
      } else {
        this.nextBtn.hidden = this.current >= maxForwardIndex;
      }
      this.nextBtn.disabled = this.animating || this.nextBtn.hidden;
    }
  }

  go(delta) {
    if (this.usesMobileSwipe() || this.slideCount < 1 || this.animating) return;

    if (delta > 0) {
      if (this.showBackground) {
        if (!this.shouldAnimate()) {
          this.instantForwardToIndex(0);
          return;
        }
        if (!this.tryAcquireNavigation()) return;
        this.animateBgToSlide();
        return;
      }

      const maxForwardIndex = this.getMaxForwardIndex();
      if (this.current >= maxForwardIndex) return;

      const nextIndex = this.current + 1;
      if (!this.shouldAnimate()) {
        this.instantForwardToIndex(nextIndex);
        return;
      }
      if (!this.tryAcquireNavigation()) return;
      this.animateTrackToIndex(nextIndex);
      return;
    }

    if (delta < 0) {
      if (this.showBackground) return;

      if (this.current > 0) {
        const prevIndex = this.current - 1;
        if (!this.shouldAnimate()) {
          this.instantBackwardToIndex(prevIndex);
          return;
        }
        if (!this.tryAcquireNavigation()) return;
        this.animateTrackToIndex(prevIndex);
        return;
      }

      if (this.current === 0 && this.hasBackground) {
        if (!this.shouldAnimate()) {
          this.instantBackwardToIndex(0, { returningToBg: true });
          return;
        }
        if (!this.tryAcquireNavigation()) return;
        this.animateSlideToBg();
      }
    }
  }

  animateBgToSlide() {
    this.runForwardToIndex(0);
  }

  animateSlideToBg() {
    this.runBackwardToIndex(0, { returningToBg: true });
  }

  animateTrackToIndex(targetIndex) {
    if (targetIndex > this.current) {
      this.runForwardToIndex(targetIndex);
      return;
    }

    this.runBackwardToIndex(targetIndex);
  }

  getMobileCardStepPx() {
    const card = this.trackCards?.[0];
    if (!card || !this.cardsTrack) return this.stepPx;

    const styles = getComputedStyle(this.cardsTrack);
    const gap = parseFloat(styles.columnGap || styles.gap) || FIC_CARD_GAP;
    return card.offsetWidth + gap;
  }

  syncCurrentFromScroll(announce = false) {
    if (!this.usesMobileSwipe() || !this.cardsTrack || !this.trackCards?.length) return;

    const step = this.getMobileCardStepPx();
    if (step <= 0) return;

    const index = Math.max(
      0,
      Math.min(this.slideCount - 1, Math.round(this.cardsTrack.scrollLeft / step))
    );

    if (index === this.current && !announce) return;

    this.current = index;
    this.announceSlide(announce);
  }

  announceSlide(announce) {
    if (!announce || !this.liveRegion) return;

    const slide = this.slides[this.current];
    const heading = slide?.querySelector('[data-fic-hero-heading]')?.textContent?.trim();
    if (heading) this.liveRegion.textContent = heading;
  }

  renderMobileSwipe(announce = false) {
    this.showBackground = true;
    this.buildTrack();

    if (this.stageTrack) {
      this.stageTrack.getAnimations().forEach((animation) => animation.cancel());
      this.stageTrack.style.transform = '';
      this.trackX = 0;
    }

    if (this.backgroundEl) this.backgroundEl.hidden = false;
    if (this.heroWrap) this.heroWrap.hidden = true;

    this.bindMobileScroll();
    this.bindTrackCardHeightSync();
    this.syncCurrentFromScroll(false);

    if (announce) {
      this.announceSlide(true);
    } else if (this.liveRegion && this.slides[this.current]) {
      const heading = this.slides[this.current]
        .querySelector('[data-fic-hero-heading]')
        ?.textContent?.trim();
      if (heading) this.liveRegion.textContent = heading;
    }

    this.updateNav();
  }

  renderHeroMobile(announce) {
    if (this.backgroundEl) this.backgroundEl.hidden = !this.showBackground;
    if (this.heroWrap) this.heroWrap.hidden = this.showBackground;

    if (this.showBackground) {
      this.updateNav();
      return;
    }

    const slide = this.slides[this.current];

    const imageSource = slide.querySelector('[data-fic-hero-image]');
    if (imageSource && this.heroImage) {
      this.cloneChildren(imageSource, this.heroImage);
      this.resolveLazyImages(this.heroImage);
    }

    const contentSource = slide.querySelector('[data-fic-hero-content]');
    if (this.heroContent && contentSource) {
      this.cloneChildren(contentSource, this.heroContent);
      this.heroContent.hidden = contentSource.hidden;
    } else if (this.heroContent) {
      this.heroContent.replaceChildren();
      this.heroContent.hidden = true;
    }

    if (this.heroWrap) {
      this.heroWrap.classList.toggle(
        'fic__hero-wrap--with-content',
        !!(contentSource && !contentSource.hidden && contentSource.innerHTML.trim())
      );
    }

    if (this.heroImage) this.applyHeroChrome(slide, this.heroImage);

    if (announce && this.liveRegion) {
      const heading = slide.querySelector('[data-fic-hero-heading]')?.textContent?.trim();
      if (heading) this.liveRegion.textContent = heading;
    }

    this.updateNav();
  }

  render(announce = false) {
    this.updateLayoutMode();

    if (this.usesTrackMotion()) {
      this.cardsTrack?.removeEventListener('scroll', this.onCardsTrackScroll);

      if (this.heroWrap) this.heroWrap.hidden = true;
      this.buildTrack();
      this.collapsePassedTrack();
      this.bindTrackCardHeightSync();

      if (announce) {
        this.announceSlide(true);
      }

      this.updateNav();
      return;
    }

    if (this.usesMobileSwipe()) {
      this.renderMobileSwipe(announce);
      return;
    }

    this.cardsTrack?.removeEventListener('scroll', this.onCardsTrackScroll);
    this.unbindTrackCardHeightSync();
    this.clearTrackCardHeights();
    if (this.stageTrack) this.stageTrack.style.transform = '';

    this.renderHeroMobile(announce);
  }
}

customElements.define('floating-image-carousel', FloatingImageCarousel);
})();
